import { CrazyGamesAdapter } from '@/sdk/CrazyGamesAdapter';
import { GameMonetizeAdapter } from '@/sdk/GameMonetizeAdapter';
import { MockPokiAdapter } from '@/sdk/MockPokiAdapter';
import type { PokiAdapter } from '@/sdk/PokiAdapter';
import { RealPokiAdapter } from '@/sdk/RealPokiAdapter';

export type PokiMode = 'mock' | 'real' | 'auto';
export type Platform = 'poki' | 'crazygames' | 'gamemonetize' | 'local';

export function resolvePokiMode(): PokiMode {
  const envMode = import.meta.env.VITE_POKI_MODE;
  if (envMode === 'mock' || envMode === 'real' || envMode === 'auto') return envMode;
  return 'auto';
}

export function resolvePlatform(): Platform {
  const envPlatform = import.meta.env.VITE_PLATFORM;
  if (
    envPlatform === 'poki' ||
    envPlatform === 'crazygames' ||
    envPlatform === 'gamemonetize' ||
    envPlatform === 'local'
  ) {
    return envPlatform;
  }
  return 'local';
}

export function createPokiAdapter(mode: PokiMode = resolvePokiMode()): PokiAdapter {
  switch (mode) {
    case 'mock':
      return new MockPokiAdapter();
    case 'real':
    case 'auto':
      return new RealPokiAdapter();
  }
}

// import.meta.env.VITE_PLATFORM is replaced at build time with a string
// literal, so each branch is compile-time-decidable. Vite/Rollup tree-shake
// the dead branches' transitive imports — a CG build doesn't pull
// RealPokiAdapter/MockPokiAdapter into the bundle, scrubbing all "PokiSDK"
// strings that would otherwise be CG "competing portal branding" risks.
export function createPlatformAdapter(
  _platform: Platform = resolvePlatform(),
  mode: PokiMode = resolvePokiMode(),
): PokiAdapter {
  if (import.meta.env.VITE_PLATFORM === 'crazygames') {
    return new CrazyGamesAdapter();
  }
  if (import.meta.env.VITE_PLATFORM === 'gamemonetize') {
    return new GameMonetizeAdapter();
  }
  if (import.meta.env.VITE_PLATFORM === 'poki') {
    return createPokiAdapter(mode);
  }
  // Default ('local' or unset) → no third-party SDK. Mirrors the vite plugin's
  // empty-script-tag default so dev/test envs without VITE_PLATFORM never
  // accidentally talk to a real ad network.
  return new MockPokiAdapter();
}

export { AdGate } from '@/sdk/AdGate';
export type {
  AdGateOptions,
  CommercialBlockedReason,
  CommercialResult,
  RewardedBlockedReason,
  RewardedResult,
} from '@/sdk/AdGate';
export { attachGlobalErrorCapture } from '@/sdk/errorWiring';
export type { CaptureError } from '@/sdk/errorWiring';
export { CrazyGamesAdapter } from '@/sdk/CrazyGamesAdapter';
export { GameMonetizeAdapter } from '@/sdk/GameMonetizeAdapter';
export { MockPokiAdapter } from '@/sdk/MockPokiAdapter';
export type { PokiAdapter, ShareableURLParams } from '@/sdk/PokiAdapter';
export { RealPokiAdapter } from '@/sdk/RealPokiAdapter';
